import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  Music4,
  Utensils,
  Languages as LangIcon,
  Sparkles,
  Palette,
  Globe2,
} from "lucide-react";
import { AUTH_SIGNUP_URL, CATEGORIES } from "@/lib/data";
import { MFT, SP } from "@/components/site/BrandLinks";

const ease = [0.22, 1, 0.36, 1];

const CULTURE_ILLUSTRATION = "/portraits/wing_culture.png";

/* Reusable sub-plates: mix of curated existing category art + a few new pexels */
const PLATE = {
  festival:
    "https://images.pexels.com/photos/1652361/pexels-photo-1652361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  food:
    "https://images.pexels.com/photos/6152389/pexels-photo-6152389.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  music:
    "https://images.pexels.com/photos/1181275/pexels-photo-1181275.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  dance:
    "https://images.pexels.com/photos/1181737/pexels-photo-1181737.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  craft:
    "https://images.pexels.com/photos/13460859/pexels-photo-13460859.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  gather:
    "https://images.pexels.com/photos/1181562/pexels-photo-1181562.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  faith:
    "https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  place:
    CATEGORIES.find((c) => c.slug === "local-history")?.image,
  letters:
    CATEGORIES.find((c) => c.slug === "family-history")?.image,
};

/* ------------------------------------------------------------------ */
/*  Primitives                                                          */
/* ------------------------------------------------------------------ */

function Chapter({ n, title, subtitle, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease }}
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20 border-t border-[color:var(--ink-line)]"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14 items-end">
        <div className="col-span-12 md:col-span-2">
          <div className="numeric-index text-[color:var(--ink-soft)]">{n}</div>
        </div>
        <div className="col-span-12 md:col-span-10">
          <h2
            className="font-serif-display leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(30px, 4vw, 58px)" }}
          >
            {title}
            {subtitle ? (
              <>
                {" "}
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  {subtitle}
                </span>
              </>
            ) : null}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="hidden md:block md:col-span-2" />
        <div className="col-span-12 md:col-span-10">{children}</div>
      </div>
    </motion.section>
  );
}

function Body({ children }) {
  return (
    <p className="text-[17px] md:text-[19px] leading-[1.65] max-w-[62ch] text-[color:var(--ink)]">
      {children}
    </p>
  );
}

function Prose({ children }) {
  return <div className="space-y-6 md:space-y-7">{children}</div>;
}

function Ledger({ items, columns = 2 }) {
  const colClass = columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <ul
      className={`mt-2 grid grid-cols-1 ${colClass} gap-x-10 gap-y-0 border-t border-[color:var(--ink-line)]`}
    >
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-baseline gap-4 py-3 border-b border-[color:var(--ink-line)]"
        >
          <span className="numeric-index text-[color:var(--ink-soft)] w-8 shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span
            className="font-serif-display leading-[1.15]"
            style={{ fontSize: "clamp(17px, 1.5vw, 22px)" }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Plate({ src, caption, caption2, aspect = "aspect-[4/5]" }) {
  return (
    <figure className="artifact border border-[color:var(--ink-line-strong)] overflow-hidden">
      <div className={`${aspect} overflow-hidden bg-[color:var(--bone-2)]`}>
        <img src={src} alt="" className="zoom w-full h-full object-cover" loading="lazy" />
      </div>
      <figcaption className="p-4 border-t border-[color:var(--ink-line)] flex items-baseline justify-between gap-4">
        <span className="numeric-index text-[color:var(--ink-soft)]">{caption}</span>
        <span className="numeric-index text-[color:var(--ink-soft)] text-right">{caption2}</span>
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/*  Content                                                             */
/* ------------------------------------------------------------------ */

const WHAT_IS = [
  "Family values", "Traditions", "Languages", "Festivals",
  "Food and recipes", "Music and dance", "Clothing", "Art and crafts",
  "Religious and spiritual beliefs", "Architecture", "Literature", "Folklore",
  "Storytelling", "Hospitality", "Community life", "Customs and etiquette",
  "Occupations", "Education", "Celebrations", "Family rituals", "Oral histories",
];

const EXPLORE_BY = [
  "Continents", "Countries", "States and provinces", "Regions",
  "Cities", "Villages", "Indigenous communities", "Ethnic groups",
  "Languages", "Religious traditions", "Historical civilizations", "Diaspora communities",
];

const LANGUAGES = [
  "Native languages", "Dialects", "Family expressions", "Traditional greetings",
  "Proverbs", "Folk songs", "Oral histories", "Stories told by elders",
  "Children's rhymes", "Family nicknames",
];

const CELEBRATIONS = [
  "Religious festivals", "National holidays", "Harvest celebrations", "Weddings",
  "Birth ceremonies", "Coming-of-age traditions", "Family reunions", "Anniversary celebrations",
  "Community festivals", "Seasonal events", "New Year traditions", "Cultural performances",
];

const FOOD = [
  "Traditional recipes", "Family cookbooks", "Holiday meals", "Regional cuisine",
  "Cooking techniques", "Agricultural traditions", "Favorite family dishes",
  "Local ingredients", "Community feasts",
];

const ARTS = [
  "Traditional music", "Folk songs", "Dance performances", "Musical instruments",
  "Paintings", "Sculpture", "Textile arts", "Pottery",
  "Weaving", "Embroidery", "Wood carving", "Architecture",
  "Poetry", "Literature", "Theater", "Storytelling",
];

const FAITH = [
  "Places of worship", "Religious traditions", "Pilgrimages", "Ceremonies",
  "Sacred texts", "Family customs", "Community celebrations", "Acts of service",
  "Charitable traditions", "Life-cycle ceremonies",
];

const FAMILY_TRADITIONS = [
  "Sunday dinners", "Annual vacations", "Family reunions", "Holiday decorations",
  "Storytelling traditions", "Handmade gifts", "Gardening", "Family businesses",
  "Volunteer work", "Community service", "Sporting traditions", "Favorite songs",
  "Birthday customs",
];

const PLACES = [
  "Villages", "Cities", "Historic neighborhoods", "Ancestral homes",
  "Markets", "Schools", "Community centers", "Historic buildings",
  "Farms", "Rivers", "Mountains", "Places of worship",
  "Cultural landmarks", "Museums", "Heritage sites",
];

const THROUGH_TIME = [
  "Migration journeys", "Changing traditions", "Blended cultures", "Multicultural families",
  "Diaspora communities", "Language evolution", "Family occupations", "New celebrations",
  "Historical milestones",
];

const CONTRIBUTIONS = [
  "Family photographs", "Videos", "Audio recordings", "Traditional recipes",
  "Festival memories", "Folk stories", "Historical documents", "Local customs",
  "Music recordings", "Dance performances", "Artwork", "Oral histories",
  "Community events",
];

const PULSE = [
  "Family Trees", "Heritage", "Celebrate a Life", "Local Communities",
  "Historic Villages", "Nature & Landscapes", "Weather Almanac", "Festivals",
  "Historic Events", "Museums", "Libraries", "Cultural Landmarks",
  "Local Businesses", "Community Organizations",
];

const WHY = [
  "It teaches us where we came from.",
  "It helps us appreciate the diversity of the world.",
  "It strengthens families and communities.",
  "It reminds us that while our customs may differ, our hopes for love, family, peace, and a better future are remarkably similar.",
];

const PILLARS = [
  { icon: LangIcon,  label: "Languages",     desc: "The voices of our ancestors" },
  { icon: Sparkles,  label: "Festivals",     desc: "Celebrations that bring us together" },
  { icon: Utensils,  label: "Food & Recipes",desc: "Flavours passed down through generations" },
  { icon: Music4,    label: "Music & Dance", desc: "Rhythms that connect the heart" },
  { icon: Palette,   label: "Art & Craft",   desc: "Expressions that inspire and connect" },
  { icon: Globe2,    label: "One World",     desc: "Many cultures. One family." },
];

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function Culture() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <main data-testid="culture-page" className="pt-32 md:pt-40">
      {/* ---------------- Masthead ---------------- */}
      <section ref={heroRef} className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="flex items-center gap-3 text-[13px] mb-8">
          <Link to="/" className="link-underline text-[color:var(--ink-soft)]">
            Index
          </Link>
          <span className="text-[color:var(--ink-soft)]">/</span>
          <span className="numeric-index">Vol. II · Culture</span>
        </div>

        <div className="overline text-[color:var(--rust)] mb-4">§ Culture</div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          style={{ y: heroY, fontSize: "clamp(52px, 10vw, 180px)" }}
          className="font-serif-display leading-[0.88] tracking-[-0.03em] max-w-6xl"
        >
          The living heritage{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            that connects families across generations.
          </span>
        </motion.h1>
      </section>

      {/* ---------------- Pull-quote ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="border-y border-[color:var(--ink)] py-10 md:py-16">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="col-span-1">
              <span
                className="font-serif-display text-[color:var(--rust)] leading-none"
                style={{ fontSize: "clamp(60px, 8vw, 120px)" }}
              >
                “
              </span>
            </div>
            <div className="col-span-11">
              <p
                className="font-italic-serif italic leading-[1.15]"
                style={{ fontSize: "clamp(24px, 4vw, 58px)" }}
              >
                Culture is not inherited through our DNA — it is passed from
                heart to heart, home to home, and generation to generation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- Opening prose (two paragraphs before the illustration) ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="hidden md:block md:col-span-2">
            <div className="overline text-[color:var(--ink-soft)]">§ Welcome</div>
          </div>
          <div className="col-span-12 md:col-span-10">
            <Prose>
              <p
                className="font-serif-display leading-[1.15]"
                style={{ fontSize: "clamp(22px, 2.4vw, 34px)" }}
              >
                Every family belongs to a culture.
              </p>

              <Body>
                Whether expressed through language, traditions, celebrations,
                food, music, art, beliefs, or everyday customs, culture shapes
                who we are and helps us understand where we come from. It
                influences how we celebrate life's milestones, care for our
                elders, raise our children, honour our ancestors, and connect
                with our communities.
              </Body>

              <Body>
                The Culture section of <MFT />, part of the <SP /> platform,
                celebrates the incredible diversity of cultures from every
                corner of the world. It provides families with a place to
                preserve traditions, document their heritage, and share the
                customs that define their unique identity.{" "}
                <span className="font-italic-serif italic">
                  Culture is not simply something we inherit — it is something
                  we live, share, and pass forward.
                </span>
              </Body>
            </Prose>
          </div>
        </div>
      </section>

      {/* ---------------- Featured illustration ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <motion.figure
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease }}
          className="relative max-w-5xl mx-auto"
        >
          <div className="border border-[color:var(--ink-line-strong)] p-3 md:p-5 bg-[color:var(--bone-2)]/50">
            <div className="overflow-hidden">
              <img
                src={CULTURE_ILLUSTRATION}
                alt="A watercolour illustration of a multigenerational family gathered around a photo album with an ancestral village, a Bharatanatyam dancer, a globe, festival photographs, food, musical instruments, textiles, and children in traditional clothing — captioned Music, Dance, Art, Stories."
                className="w-full h-auto block"
                loading="lazy"
                data-testid="culture-illustration"
              />
            </div>
          </div>
          <figcaption className="mt-4 flex items-start justify-between gap-6">
            <div className="numeric-index text-[color:var(--ink-soft)]">
              Plate № C · Our Stories · Our Traditions · Our World
            </div>
            <div className="numeric-index text-[color:var(--ink-soft)] text-right max-w-[320px] hidden md:block">
              One world · Many cultures · One family
            </div>
          </figcaption>
        </motion.figure>
      </section>

      {/* ---------------- Chapter 01 · What Is Culture? ---------------- */}
      <Chapter n="01" title="What is culture?" subtitle="The collective story of humanity.">
        <Prose>
          <Body>Culture is the collective story of humanity. It is expressed through:</Body>
          <Ledger items={WHAT_IS} columns={3} />
          <p
            className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3]"
            style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
          >
            Every culture tells a unique story while contributing to our shared human heritage.
          </p>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 02 · Every Family Has a Culture ---------------- */}
      <Chapter n="02" title="Every family" subtitle="has a culture.">
        <Prose>
          <Body>Culture begins at home.</Body>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 pt-2">
            <div className="lg:col-span-7">
              <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2">
                {[
                  "Children learn their first language from their parents.",
                  "Recipes are handed down through generations.",
                  "Songs are sung at family gatherings.",
                  "Stories are told around dinner tables.",
                  "Grandparents pass on wisdom that cannot be found in books.",
                ].map((line, i) => (
                  <p
                    key={i}
                    className="font-italic-serif italic leading-[1.4]"
                    style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
                  >
                    {line}
                  </p>
                ))}
              </div>
              <p className="mt-8 text-[17px] md:text-[19px] leading-[1.65]">
                These everyday moments become the traditions that define a family.
                Whether your family celebrates{" "}
                {[
                  "Diwali",
                  "Christmas",
                  "Eid",
                  "Hanukkah",
                  "Lunar New Year",
                  "Nowruz",
                  "Kwanzaa",
                  "Día de los Muertos",
                  "Vesak",
                  "Thanksgiving",
                ].map((f, i, arr) => (
                  <span
                    key={f}
                    className="font-italic-serif italic text-[color:var(--rust)]"
                  >
                    {f}
                    {i < arr.length - 1 ? ", " : ""}
                  </span>
                ))}
                , or local harvest festivals, every celebration becomes part of
                your family's cultural identity.
              </p>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.gather}
                caption="Plate № 001"
                caption2="Gathered · Undated"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 03 · A World of Cultures ---------------- */}
      <Chapter n="03" title="A world" subtitle="of cultures.">
        <Prose>
          <Body>Humanity's greatest strength is its diversity.</Body>
          <Body>
            Across the world, cultures have evolved in response to geography,
            history, language, climate, beliefs, and shared experiences.
          </Body>
          <Body>Explore cultures by:</Body>
          <Ledger items={EXPLORE_BY} columns={3} />
          <Body>
            Discover how families from different backgrounds celebrate life
            while sharing universal values of love, belonging, respect, and
            community.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 04 · Languages ---------------- */}
      <Chapter n="04" title="Languages," subtitle="the voice of heritage.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>Language carries history.</Body>
              <Body>
                Every word, proverb, poem, and lullaby reflects generations of
                knowledge and experience.
              </Body>
              <Body>Preserve:</Body>
              <Ledger items={LANGUAGES} columns={2} />
              <p
                className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3] mt-6"
                style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
              >
                Recording these voices today helps preserve them for tomorrow.
              </p>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.letters}
                caption="Plate № 002"
                caption2="In a mother tongue"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 05 · Celebrations ---------------- */}
      <Chapter n="05" title="Celebrations that" subtitle="bring families together.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.festival}
                caption="Plate № 003"
                caption2="Festival of lights"
              />
            </div>
            <div className="lg:col-span-7">
              <Body>
                Festivals and celebrations strengthen family bonds and create
                lasting memories.
              </Body>
              <Body>Document:</Body>
              <Ledger items={CELEBRATIONS} columns={2} />
              <p
                className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3] mt-6"
                style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
              >
                Every celebration reflects the values and identity of the
                people who keep it alive.
              </p>
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 06 · Food ---------------- */}
      <Chapter n="06" title="Food," subtitle="a taste of family history.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>Recipes often tell stories that history books cannot.</Body>
              <Body>
                A simple family meal may preserve centuries of migration,
                trade, agriculture, and cultural exchange.
              </Body>
              <Body>Record:</Body>
              <Ledger items={FOOD} columns={2} />
              <p
                className="font-italic-serif italic text-[color:var(--rust)] leading-[1.3] mt-6"
                style={{ fontSize: "clamp(22px, 2.4vw, 32px)" }}
              >
                Food connects generations through shared memories and traditions.
              </p>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.food}
                caption="Plate № 004"
                caption2="A plate, remembered"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 07 · Music, Dance & Arts ---------------- */}
      <Chapter n="07" title="Music, dance," subtitle="and the arts.">
        <Prose>
          <Body>Culture is expressed through creativity.</Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 pt-4 pb-4">
            <Plate src={PLATE.music} caption="Plate № 005" caption2="A rhythm, kept" aspect="aspect-[4/3]" />
            <Plate src={PLATE.dance} caption="Plate № 006" caption2="A step, remembered" aspect="aspect-[4/3]" />
          </div>
          <Body>Preserve:</Body>
          <Ledger items={ARTS} columns={3} />
          <Body>
            These artistic traditions become lasting expressions of family and
            community identity.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 08 · Faith and Beliefs ---------------- */}
      <Chapter n="08" title="Faith" subtitle="and beliefs.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.faith}
                caption="Plate № 007"
                caption2="At the threshold"
              />
            </div>
            <div className="lg:col-span-7">
              <Body>
                Throughout history, spiritual and religious traditions have
                shaped communities and family life.
              </Body>
              <Body>Respectfully document:</Body>
              <Ledger items={FAITH} columns={2} />
              <Body>
                MyFamilyTree celebrates cultural diversity by providing space
                for families of every faith, belief, and background to preserve
                their traditions with respect and authenticity.
              </Body>
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 09 · Family Traditions ---------------- */}
      <Chapter n="09" title="Family" subtitle="traditions.">
        <Prose>
          <Body>
            Every family has traditions that may seem ordinary today but
            become extraordinary over time.
          </Body>
          <Body>These might include:</Body>
          <Ledger items={FAMILY_TRADITIONS} columns={3} />
          <p
            className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3]"
            style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
          >
            These moments create the memories that future generations will treasure.
          </p>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 10 · Culture and Place ---------------- */}
      <Chapter n="10" title="Culture" subtitle="and place.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>Culture is deeply connected to geography.</Body>
              <Body>Document connections to:</Body>
              <Ledger items={PLACES} columns={2} />
              <Body>Every place contributes to a family's story.</Body>
              <div className="mt-4">
                <Link
                  to="/cemeteries"
                  className="btn-ghost text-[13px]"
                  data-testid="culture-cemeteries-cta"
                >
                  Browse the cemeteries directory <span aria-hidden>→</span>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate src={PLATE.place} caption="Plate № 008" caption2="A room, a hallway, a life" />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 11 · Culture Through Time ---------------- */}
      <Chapter n="11" title="Culture" subtitle="through time.">
        <Prose>
          <Body>Culture evolves while remaining connected to its roots.</Body>
          <Body>
            Families adapt to new countries, new technologies, and new
            opportunities, yet many traditions continue across generations.
          </Body>
          <Body>
            <MFT /> helps document:
          </Body>
          <Ledger items={THROUGH_TIME} columns={2} />
          <Body>
            By recording these changes, families preserve both their origins
            and their evolving identity.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 12 · Living Archive ---------------- */}
      <Chapter n="12" title="A living" subtitle="cultural archive.">
        <Prose>
          <Body>Culture is strongest when it is shared.</Body>
          <Body>Invite relatives and community members to contribute:</Body>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Ledger items={CONTRIBUTIONS} columns={2} />
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate src={PLATE.craft} caption="Plate № 009" caption2="A hand at work" />
            </div>
          </div>
          <Body>
            Together, these contributions create a living archive that grows
            richer with every generation.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 13 · Connected Through SocialPulse ---------------- */}
      <Chapter n="13" title="Connected through" subtitle="SocialPulse.">
        <Prose>
          <Body>
            The Culture section is integrated across the entire <SP />
            ecosystem. Explore related:
          </Body>
          <Ledger items={PULSE} columns={2} />
          <Body>
            By connecting people, places, traditions, and history, <SP />
            creates a dynamic network of cultural knowledge that spans the
            globe.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 14 · Why Culture Matters ---------------- */}
      <Chapter n="14" title="Why culture" subtitle="matters.">
        <Prose>
          <Body>Culture gives us a sense of identity and belonging.</Body>
          <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2 my-2">
            {WHY.map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 pt-6">
            {[
              "When cultures are preserved, humanity becomes richer.",
              "When stories are shared, understanding grows.",
              "When traditions are celebrated, generations remain connected.",
            ].map((line, i) => (
              <div key={i} className="border-t border-[color:var(--ink)] pt-4">
                <div className="numeric-index text-[color:var(--ink-soft)] mb-2">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p
                  className="font-serif-display leading-[1.15]"
                  style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
                >
                  {line}
                </p>
              </div>
            ))}
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Six pillars strip ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-24 border-t border-[color:var(--ink-line)]">
        <div className="overline text-[color:var(--ink-soft)] mb-8">
          § Preserve · Share · Inspire
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border-l border-t border-[color:var(--ink-line)]">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.05, ease }}
                className="border-r border-b border-[color:var(--ink-line)] p-5 md:p-6 min-h-[200px] flex flex-col justify-between bg-[color:var(--bone)] hover:bg-[color:var(--bone-2)]"
                style={{ transition: "background-color 400ms ease" }}
                data-testid={`culture-pillar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-10 h-10 rounded-full border border-[color:var(--ink)] flex items-center justify-center text-[color:var(--rust)]">
                  <Icon size={18} />
                </div>
                <div>
                  <div
                    className="font-serif-display leading-[1] mt-5"
                    style={{ fontSize: "clamp(18px, 1.7vw, 24px)" }}
                  >
                    {p.label}
                  </div>
                  <p className="mt-2 text-[12.5px] leading-[1.5] text-[color:var(--ink-soft)]">
                    {p.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ---------------- Closing manifesto ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-[color:var(--ink)]">
        <div className="overline text-[color:var(--ink-soft)] mb-6">
          § Preserve your culture. Share your story.
        </div>
        <p
          className="font-serif-display leading-[1.05] max-w-4xl mb-10"
          style={{ fontSize: "clamp(28px, 3.6vw, 54px)" }}
        >
          The Culture section of MyFamilyTree is more than a collection of
          articles — it is a{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            living record of humanity's shared heritage.
          </span>
        </p>

        <Body>
          Whether you are documenting an ancestral language, preserving a
          cherished family recipe, recording a traditional song, sharing a
          festival celebration, or telling the story of your community, your
          contributions help build a richer understanding of our world.
        </Body>

        <div className="space-y-3 md:space-y-4 mt-12 mb-10">
          {[
            "Every family has a culture.",
            "Every culture has a story.",
            "Every story deserves to be remembered.",
          ].map((line, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.06, ease }}
              className="font-serif-display leading-[1.05]"
              style={{ fontSize: "clamp(24px, 3.4vw, 48px)" }}
            >
              {line}
            </motion.p>
          ))}
        </div>

        <Body>
          Together, we can create a global archive that celebrates the
          diversity, beauty, and shared humanity of people everywhere.
        </Body>

        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href={AUTH_SIGNUP_URL}
            className="btn-rust"
            data-testid="culture-cta-primary"
          >
            Begin your culture archive <span aria-hidden>→</span>
          </a>
          <Link to="/category/heritage" className="btn-ghost" data-testid="culture-cta-heritage">
            Read the Heritage article
          </Link>
          <Link to="/category/family-stories" className="btn-ghost" data-testid="culture-cta-stories">
            Explore family stories
          </Link>
        </div>
      </section>

      {/* ---------------- Signature ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
        <div className="border-t border-[color:var(--ink-line)] pt-10">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7">
              <div
                className="font-serif-display leading-[0.95]"
                style={{ fontSize: "clamp(40px, 6vw, 96px)" }}
              >
                MyFamilyTree
                <span className="text-[color:var(--rust)]"> · </span>
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  Culture
                </span>
              </div>
              <div
                className="font-italic-serif italic mt-3 text-[color:var(--ink-soft)]"
                style={{ fontSize: "clamp(16px, 1.8vw, 22px)" }}
              >
                Celebrating the traditions that unite families across the world.
              </div>
            </div>
            <div className="col-span-12 md:col-span-5 md:text-right">
              <p
                className="font-italic-serif italic text-[color:var(--ink)] leading-[1.35]"
                style={{ fontSize: "clamp(16px, 1.6vw, 20px)" }}
              >
                Preserve your culture. Honour your heritage. Inspire future
                generations.
              </p>
              <div className="mt-6 numeric-index text-[color:var(--ink-soft)]">
                Document today · Celebrate always · Inspire forever.
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
